/**
 * InReviewTab — Truck Pusher InReview queue (Phase 11 / Task 11.1, DEC-120).
 *
 * Lists every ticket currently in IN_REVIEW for the active tenant: HaulId, site/
 * operator, driver, net BBL, entered-review time, flag chips, and a STALE badge
 * for escalated tickets (sorted to the top). Filter by flag type / operator /
 * driver / date range / stale-only; sort by Date | Flags | Volume | Driver.
 *
 * Row click opens the full review detail panel (Task 11.2, InReviewDetailPanel).
 * All timestamps render in the dispatcher's local timezone (DEC-004).
 */

import * as React from 'react';
import { useApiOpts } from '@lodex-pro/web-shared';
import { INREVIEW_FLAG_CODES } from '@lodex-pro/shared/inreview';
import { useUserTimezone, useUserProfile } from '../../lib/use-user-timezone';
import { formatCreated } from '../../lib/timestamp';
import {
  useInReviewQueue,
  useEscalatedInReviewCount,
  type InReviewQueueParams,
  type InReviewSortKey,
} from './use-inreview';
import { FLAG_META, FlagChip, StaleBadge } from './flag-meta';
import { InReviewDetailPanel } from './InReviewDetailPanel';
import { formatNumber } from '@lodex-pro/shared/number-format';
import { useUrlDetail, useUrlFlag, useUrlParam } from '../../lib/url-state';

const SORT_OPTIONS: ReadonlyArray<{ key: InReviewSortKey; label: string }> = [
  { key: 'date', label: 'Date' },
  { key: 'flags', label: 'Flags' },
  { key: 'volume', label: 'Volume' },
  { key: 'driver', label: 'Driver' },
];

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: '11px',
  fontWeight: 700,
  color: 'var(--color-text-tertiary)',
  borderBottom: '1px solid var(--color-border-primary)',
  whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: '13px',
  color: 'var(--color-text-primary)',
  borderBottom: '1px solid var(--color-border-primary)',
  verticalAlign: 'middle',
};
const inputStyle: React.CSSProperties = {
  height: '32px',
  padding: '0 8px',
  fontSize: '13px',
  borderRadius: '10px',
  border: '1px solid var(--color-border-primary)',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
};

export function InReviewTab(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const tz = useUserTimezone();

  // OPEN-191 — the filters live in the URL (`review*`: the Job Board's `q`/`driver`/`status`
  // share '/'), so they survive the detail, a refresh and a shared link. The sort bar stays local.
  const [flagType, setFlagType] = useUrlParam('reviewFlag', '', []);
  const [driverId, setDriverId] = useUrlParam('reviewDriver', '', []);
  const [operatorId, setOperatorId] = useUrlParam('reviewOperator', '', []);
  const [dateFrom, setDateFrom] = useUrlParam('reviewFrom', '', []);
  const [dateTo, setDateTo] = useUrlParam('reviewTo', '', []);
  const [staleOnly, setStaleOnly] = useUrlFlag('reviewStale', []);
  const [sort, setSort] = React.useState<InReviewSortKey>('date');
  // OPEN-191 — the open ticket is `?jobId=`, the same param as OPEN-144's deep link
  // (`?tab=review&jobId=`, the job modal's parked badge), so that link simply opens it.
  const [selectedJobId, setSelectedJobId] = useUrlDetail('jobId');

  // Task 11.6 — TENANT_ADMIN escalation banner. The count is the tickets in review
  // now with the ESCALATED flag — exactly what "View escalated tickets" filters to
  // (OPEN-152: it used to count stale notifications, so the list could be empty).
  // While that filter is on, the banner gives way to the "Showing escalated
  // tickets · Show all" bar; it comes back when the filter is cleared.
  const { data: profileData } = useUserProfile();
  const isTenantAdmin = profileData?.profile.role === 'TENANT_ADMIN';
  const { data: escalatedData } = useEscalatedInReviewCount(isTenantAdmin === true);
  const escalationCount = escalatedData?.total ?? 0;
  const showingEscalated = flagType === 'ESCALATED';
  const showEscalationBanner = isTenantAdmin === true && !showingEscalated && escalationCount > 0;

  const params: InReviewQueueParams = React.useMemo(
    () => ({
      flagType: flagType === '' ? [] : [flagType],
      operatorId,
      driverId,
      dateFrom,
      dateTo,
      tz,
      staleOnly,
      sort,
    }),
    [flagType, operatorId, driverId, dateFrom, dateTo, tz, staleOnly, sort],
  );

  const { data, isLoading, isError } = useInReviewQueue(apiOpts, params);
  const tickets = data?.tickets ?? [];

  // Full-page review panel replaces the queue when a ticket is selected (11.2).
  if (selectedJobId !== null) {
    const selectedRow = tickets.find((t) => t.id === selectedJobId);
    return (
      <InReviewDetailPanel
        apiOpts={apiOpts}
        jobId={selectedJobId}
        stale={selectedRow?.isStale ?? false}
        onBack={(): void => {
          setSelectedJobId(null);
        }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Task 11.6 — TENANT_ADMIN auto-escalation banner. */}
      {showEscalationBanner && (
        <div
          role="alert"
          data-testid="escalation-banner"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 16px',
            backgroundColor: 'rgba(246, 182, 94,0.12)',
            borderBottom: '1px solid var(--color-warning)',
            color: 'var(--color-warning)',
            fontSize: '13px',
            flexShrink: 0,
          }}
        >
          <span aria-hidden="true">⚠</span>
          <span style={{ fontWeight: 600 }}>
            {formatNumber(escalationCount)} ticket{escalationCount === 1 ? ' is' : 's are'}{' '}
            escalated and {escalationCount === 1 ? 'needs' : 'need'} your attention.
          </span>
          <button
            type="button"
            data-testid="view-escalated-button"
            onClick={(): void => {
              setFlagType('ESCALATED');
            }}
            style={{
              marginLeft: 'auto',
              padding: '5px 12px',
              fontSize: '12px',
              fontWeight: 700,
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-warning)',
              background: 'none',
              color: 'var(--color-warning)',
              cursor: 'pointer',
            }}
          >
            View escalated tickets
          </button>
        </div>
      )}

      {/* OPEN-152 — the way back out of the escalated view, without hunting for the dropdown. */}
      {showingEscalated && (
        <div
          data-testid="escalated-filter-bar"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 16px',
            backgroundColor: 'rgba(246, 182, 94,0.08)',
            borderBottom: '1px solid var(--color-border-primary)',
            color: 'var(--color-text-primary)',
            fontSize: '13px',
            flexShrink: 0,
          }}
        >
          <span style={{ fontWeight: 600 }}>Showing escalated tickets</span>
          <span aria-hidden="true" style={{ color: 'var(--color-text-tertiary)' }}>
            ·
          </span>
          <button
            type="button"
            data-testid="show-all-flags-button"
            onClick={(): void => {
              setFlagType('');
            }}
            style={{
              padding: '5px 12px',
              fontSize: '12px',
              fontWeight: 700,
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border-primary)',
              background: 'none',
              color: 'var(--color-accent-blue)',
              cursor: 'pointer',
            }}
          >
            Show all
          </button>
        </div>
      )}

      {/* Filter + sort bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
          padding: '10px 16px',
          borderBottom: '1px solid var(--color-border-primary)',
          backgroundColor: 'var(--color-bg-secondary)',
          flexShrink: 0,
        }}
      >
        <select
          aria-label="Filter by flag type"
          value={flagType}
          onChange={(e): void => {
            setFlagType(e.target.value);
          }}
          style={inputStyle}
        >
          <option value="">All flags</option>
          <option value="NONE">No flags</option>
          {INREVIEW_FLAG_CODES.map((code) => (
            <option key={code} value={code}>
              {FLAG_META[code].label}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Operator ID"
          aria-label="Filter by operator"
          value={operatorId}
          onChange={(e): void => {
            setOperatorId(e.target.value.trim());
          }}
          style={{ ...inputStyle, width: '160px' }}
        />
        <input
          type="text"
          placeholder="Driver ID"
          aria-label="Filter by driver"
          value={driverId}
          onChange={(e): void => {
            setDriverId(e.target.value.trim());
          }}
          style={{ ...inputStyle, width: '160px' }}
        />
        <input
          type="date"
          aria-label="Entered review from"
          value={dateFrom}
          onChange={(e): void => {
            setDateFrom(e.target.value);
          }}
          style={inputStyle}
        />
        <input
          type="date"
          aria-label="Entered review to"
          value={dateTo}
          onChange={(e): void => {
            setDateTo(e.target.value);
          }}
          style={inputStyle}
        />
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '13px',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={staleOnly}
            onChange={(e): void => {
              setStaleOnly(e.target.checked);
            }}
          />
          Stale only
        </label>

        <div
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>Sort</span>
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={(): void => {
                setSort(opt.key);
              }}
              aria-pressed={sort === opt.key}
              style={{
                height: '30px',
                padding: '0 10px',
                fontSize: '12px',
                fontWeight: 600,
                borderRadius: 'var(--radius-pill)',
                border: 'none',
                cursor: 'pointer',
                backgroundColor:
                  sort === opt.key ? 'var(--color-accent-blue)' : 'var(--color-bg-primary)',
                color: sort === opt.key ? 'var(--color-on-accent)' : 'var(--color-text-secondary)',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Queue table */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Ticket ID</th>
              <th style={thStyle}>Site / Operator</th>
              <th style={thStyle}>Driver</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>BBL</th>
              <th style={thStyle}>Entered InReview</th>
              <th style={thStyle}>Flags</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td style={{ ...tdStyle, textAlign: 'center' }} colSpan={7}>
                  Loading…
                </td>
              </tr>
            )}
            {isError && !isLoading && (
              <tr>
                <td
                  style={{ ...tdStyle, textAlign: 'center', color: 'var(--color-danger)' }}
                  colSpan={7}
                >
                  Failed to load the InReview queue.
                </td>
              </tr>
            )}
            {!isLoading && !isError && tickets.length === 0 && (
              <tr>
                <td
                  style={{ ...tdStyle, textAlign: 'center', color: 'var(--color-text-tertiary)' }}
                  colSpan={7}
                >
                  No tickets pending review
                </td>
              </tr>
            )}
            {tickets.map((t) => (
              <tr
                key={t.id}
                onClick={(): void => {
                  setSelectedJobId(t.id);
                }}
                style={{
                  cursor: 'pointer',
                  backgroundColor: t.isStale ? 'rgba(246, 182, 94,0.07)' : 'transparent',
                }}
              >
                <td style={{ ...tdStyle, fontWeight: 600 }}>{t.haulId ?? '—'}</td>
                <td style={tdStyle}>
                  <div>{t.siteName ?? '—'}</div>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
                    {t.operatorName ?? '—'}
                  </div>
                </td>
                <td style={tdStyle}>{t.driverName ?? '—'}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {formatNumber(t.netBbl, { decimals: 1 })}
                </td>
                <td style={tdStyle}>
                  {t.inreviewAt !== null ? formatCreated(t.inreviewAt, tz) : '—'}
                </td>
                <td style={tdStyle}>
                  {t.isStale && (
                    <span style={{ marginRight: '4px' }}>
                      <StaleBadge />
                    </span>
                  )}
                  {t.inreviewFlags.length === 0 ? (
                    <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                      Clean
                    </span>
                  ) : (
                    t.inreviewFlags.map((code) => <FlagChip key={code} code={code} />)
                  )}
                </td>
                <td style={tdStyle}>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                    In Review
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
